/**
 * Control Plane Service
 *
 * Persists capability overrides, system settings, audit log,
 * users, and tenants to Supabase.
 * Fails gracefully — all errors are logged but never thrown.
 *
 * Required tables:
 *   cp_system_settings      — singleton row (id = 'global')
 *   cp_capability_overrides — one row per active capability override
 *   cp_audit_log            — append-only governance audit trail
 *   cp_users                — managed user accounts
 *   cp_tenants              — managed tenant accounts
 */

import { supabase } from "@/lib/supabase";
import type {
  ManagedUser, ManagedTenant, ControlPlaneAuditEntry,
  BehaviorMode, Environment, ModeProfileId,
} from "@/data/controlPlane/types";
import {
  toDbSystemSettings, toUiSystemSettings,
  toDbOverride, toUiOverrides,
  toDbAuditEntry, toUiAuditEntry,
  toDbUser, toUiUser,
  toDbTenant, toUiTenant,
  type CpSystemSettingsRow,
  type CpCapabilityOverrideRow,
  type CpAuditLogRow,
  type CpUserRow,
  type CpTenantRow,
} from "@/mappers/control-plane.mapper";

export const ControlPlaneService = {
  // ── System settings ─────────────────────────────────────────────────────────

  async loadSystemSettings(): Promise<{ environment: Environment; modeProfile: ModeProfileId } | null> {
    try {
      const { data, error } = await supabase
        .from("cp_system_settings")
        .select("*")
        .eq("id", "global")
        .maybeSingle();

      if (error) {
        console.warn("[ControlPlaneService.loadSystemSettings] DB error:", error.message);
        return null;
      }
      return data ? toUiSystemSettings(data as CpSystemSettingsRow) : null;
    } catch (err) {
      console.warn("[ControlPlaneService.loadSystemSettings] Exception:", err);
      return null;
    }
  },

  async saveSystemSettings(environment: Environment, modeProfile: ModeProfileId): Promise<void> {
    try {
      const { error } = await supabase
        .from("cp_system_settings")
        .upsert(toDbSystemSettings(environment, modeProfile));
      if (error) console.warn("[ControlPlaneService.saveSystemSettings] DB error:", error.message);
    } catch (err) {
      console.warn("[ControlPlaneService.saveSystemSettings] Exception:", err);
    }
  },

  // ── Capability overrides ─────────────────────────────────────────────────────

  async loadOverrides(): Promise<Record<string, { enabled: boolean; behaviorMode: BehaviorMode }>> {
    try {
      const { data, error } = await supabase
        .from("cp_capability_overrides")
        .select("*");

      if (error) {
        console.warn("[ControlPlaneService.loadOverrides] DB error:", error.message);
        return {};
      }
      return toUiOverrides((data ?? []) as CpCapabilityOverrideRow[]);
    } catch (err) {
      console.warn("[ControlPlaneService.loadOverrides] Exception:", err);
      return {};
    }
  },

  async upsertOverride(capabilityId: string, enabled: boolean, mode: BehaviorMode): Promise<void> {
    try {
      const { error } = await supabase
        .from("cp_capability_overrides")
        .upsert(toDbOverride(capabilityId, enabled, mode), { onConflict: "capability_id" });
      if (error) console.warn("[ControlPlaneService.upsertOverride] DB error:", error.message);
    } catch (err) {
      console.warn("[ControlPlaneService.upsertOverride] Exception:", err);
    }
  },

  async deleteOverride(capabilityId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from("cp_capability_overrides")
        .delete()
        .eq("capability_id", capabilityId);
      if (error) console.warn("[ControlPlaneService.deleteOverride] DB error:", error.message);
    } catch (err) {
      console.warn("[ControlPlaneService.deleteOverride] Exception:", err);
    }
  },

  // ── Audit log ────────────────────────────────────────────────────────────────

  async saveAuditEntry(entry: ControlPlaneAuditEntry): Promise<void> {
    try {
      const { error } = await supabase
        .from("cp_audit_log")
        .insert(toDbAuditEntry(entry));
      if (error) console.warn("[ControlPlaneService.saveAuditEntry] DB error:", error.message);
    } catch (err) {
      console.warn("[ControlPlaneService.saveAuditEntry] Exception:", err);
    }
  },

  async loadAuditLog(limit = 100): Promise<ControlPlaneAuditEntry[]> {
    try {
      const { data, error } = await supabase
        .from("cp_audit_log")
        .select("*")
        .order("timestamp", { ascending: false })
        .limit(limit);

      if (error) {
        console.warn("[ControlPlaneService.loadAuditLog] DB error:", error.message);
        return [];
      }
      return ((data ?? []) as CpAuditLogRow[]).map(toUiAuditEntry);
    } catch (err) {
      console.warn("[ControlPlaneService.loadAuditLog] Exception:", err);
      return [];
    }
  },

  // ── Users ────────────────────────────────────────────────────────────────────

  async loadUsers(): Promise<ManagedUser[]> {
    try {
      const { data, error } = await supabase
        .from("cp_users")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) {
        console.warn("[ControlPlaneService.loadUsers] DB error:", error.message);
        return [];
      }
      return ((data ?? []) as CpUserRow[]).map(toUiUser);
    } catch (err) {
      console.warn("[ControlPlaneService.loadUsers] Exception:", err);
      return [];
    }
  },

  async upsertUser(user: ManagedUser): Promise<void> {
    try {
      const { error } = await supabase
        .from("cp_users")
        .upsert(toDbUser(user));
      if (error) console.warn("[ControlPlaneService.upsertUser] DB error:", error.message);
    } catch (err) {
      console.warn("[ControlPlaneService.upsertUser] Exception:", err);
    }
  },

  // ── Tenants ──────────────────────────────────────────────────────────────────

  async loadTenants(): Promise<ManagedTenant[]> {
    try {
      const { data, error } = await supabase
        .from("cp_tenants")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) {
        console.warn("[ControlPlaneService.loadTenants] DB error:", error.message);
        return [];
      }
      return ((data ?? []) as CpTenantRow[]).map(toUiTenant);
    } catch (err) {
      console.warn("[ControlPlaneService.loadTenants] Exception:", err);
      return [];
    }
  },

  async upsertTenant(tenant: ManagedTenant): Promise<void> {
    try {
      const { error } = await supabase
        .from("cp_tenants")
        .upsert(toDbTenant(tenant));
      if (error) console.warn("[ControlPlaneService.upsertTenant] DB error:", error.message);
    } catch (err) {
      console.warn("[ControlPlaneService.upsertTenant] Exception:", err);
    }
  },
};
